import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { HardHat, Camera, FileText, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * LO QUE MANDÓ EL CONTRATISTA, DONDE SE TRABAJA EL CLAIM.
 *
 * Pedido de Manuel (2026-08-26): "también está uploading files, quiero que eso me salga como un
 * módulo en intake de files from contractor, y en claim review también, para verlos todos".
 *
 * De dónde salió: el contratista de LOUKISHA WALKER subió su declaration page desde JobFlow y en
 * el CRM no se veía por ningún lado — el archivo estaba guardado (`claim_files` con el source_tag
 * "From JobFlow — NavyRoofing"), pero enterrado entre los 20.959 archivos de la pestaña Files, que
 * es la última que alguien abre mientras revisa un claim nuevo.
 *
 * Medido ese día: TODO lo que llega por esa vía son PDFs (dec pages, TARPs, estimados). Las FOTOS
 * llegan por otro camino (`claim_photo_links`, 1.086 del contratista) y por eso se muestran acá
 * también: para quien revisa, "lo que mandó el contratista" es una sola pregunta, no dos.
 *
 * Se dibuja SOLO si hay algo. Un módulo vacío en cada claim es ruido que enseña a saltearlo.
 */
type Archivo = { id: string; file_name: string; doc_label: string | null; source_tag: string | null; created_at: string };
type Link = { id: string; url: string; note: string | null; category: string | null; created_at: string; deleted_at?: string | null };

export function ContractorFilesPanel({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [archivos, setArchivos] = useState<Archivo[]>([]);
  const [links, setLinks] = useState<Link[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let vivo = true;
    // El mismo endpoint que la pestaña Files: sin una consulta nueva que mantener en paralelo.
    api.get<{ files: Archivo[] }>(`/api/claims/${claimId}/files`)
      .then((r) => { if (vivo) setArchivos((r.files ?? []).filter((f) => (f.source_tag ?? '').startsWith('From JobFlow'))); })
      .catch(() => { /* el módulo se esconde solo si no hay nada */ });
    api.get<{ links: Link[] }>(`/api/claims/${claimId}/photo-links`)
            // Sin los borrados: un link que alguien quitó a propósito no vuelve por otra pantalla.
      .then((r) => { if (vivo) setLinks((r.links ?? []).filter((l) => l.category === 'contractor' && !l.deleted_at)); })
      .catch(() => undefined);
    return () => { vivo = false; };
  }, [claimId]);

  if (!archivos.length && !links.length) return null;

  /** La URL firmada se pide al abrir, no al listar: son links con vencimiento. */
  async function abrir(id: string) {
    const ya = urls[id];
    if (ya) { window.open(ya, '_blank', 'noopener'); return; }
    try {
      const r = await api.get<{ url: string }>(`/api/files/${id}/url`);
      setUrls((m) => ({ ...m, [id]: r.url }));
      window.open(r.url, '_blank', 'noopener');
    } catch { /* si R2 no responde, el resto del módulo sigue usable */ }
  }

  const cuando = (v: string) => new Date(v).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  /** "From JobFlow — NavyRoofing" → "NavyRoofing". El prefijo es ruido repetido en cada fila. */
  const deQuien = (tag: string | null) => (tag ?? '').replace(/^From JobFlow\s*[—-]\s*/, '').trim();

  return (
    <div className="card" style={{ padding: 14, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 'var(--fs-sm)' }}>
          <HardHat size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cfp.title')}
        </b>
        <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{archivos.length + links.length}</span>
      </div>

      {archivos.map((f) => (
        <button key={f.id} type="button" onClick={() => void abrir(f.id)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
            background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8,
            padding: '7px 9px', cursor: 'pointer', color: 'inherit' }}>
          <FileText size={13} strokeWidth={1.75} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.doc_label || f.file_name}
          </span>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', flexShrink: 0 }}>{deQuien(f.source_tag)} · {cuando(f.created_at)}</span>
        </button>
      ))}

      {links.map((l) => (
        <a key={l.id} href={l.url} target="_blank" rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8,
            padding: '7px 9px', textDecoration: 'none', color: 'inherit' }}>
          <Camera size={13} strokeWidth={1.75} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l.note?.trim() || t('cfp.photoAlbum')}
          </span>
          <ExternalLink size={12} strokeWidth={1.75} style={{ flexShrink: 0, opacity: 0.6 }} />
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', flexShrink: 0 }}>{cuando(l.created_at)}</span>
        </a>
      ))}
    </div>
  );
}
